import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { EdgeFunctionError } from '@/lib/api/edgeFunction';
import { inviteTeamMember, listTeam, revokeTeamMember } from '@/lib/api/teamInvite';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/team')({
  component: TeamPage,
});

function TeamPage() {
  const { supabase } = useAuth();
  const { businessId } = useProperty();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Scoped by business_id — every query key for team/booking/rate data must
  // be, so switching properties never shows the wrong team (plan L346-348).
  const teamQuery = useQuery({
    queryKey: ['team', businessId],
    queryFn: () => listTeam(supabase, businessId as string),
    enabled: !!businessId,
  });

  // An invite during the list's first load must still show up (team.test.tsx).
  const refreshTeam = () => cancelThenInvalidate(queryClient, { queryKey: ['team', businessId] });

  function handleAal2Redirect(err: unknown): boolean {
    if (
      err instanceof EdgeFunctionError &&
      (err.code === 'aal2_required' || err.code === 'mfa_enrollment_required')
    ) {
      navigate({ to: '/sign-in/mfa', search: { next: '/team' } });
      return true;
    }
    return false;
  }

  const inviteMutation = useMutation({
    mutationFn: (inviteEmail: string) => inviteTeamMember(supabase, businessId as string, inviteEmail),
    onSuccess: () => {
      setEmail('');
      setFormError(null);
      void refreshTeam();
    },
    onError: (err) => {
      if (handleAal2Redirect(err)) return;
      setFormError(err instanceof Error ? err.message : 'Could not send the invite');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (memberId: string) => revokeTeamMember(supabase, businessId as string, memberId),
    onSuccess: () => void refreshTeam(),
    onError: (err) => {
      handleAal2Redirect(err);
    },
  });

  if (!businessId) {
    return (
      <section className="rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Team</h1>
        <p className="mt-sm text-body text-ink-2">Select a property to manage its team.</p>
      </section>
    );
  }

  return (
    <section className="rounded-card border border-border bg-surface p-xl shadow-card">
      <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Team</h1>
      <p className="mt-sm text-body text-ink-2">
        Reception staff can see bookings and check guests in. They cannot open settlement settings or the listing
        editor.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          inviteMutation.mutate(email);
        }}
        className="mt-lg flex items-end gap-sm"
      >
        <label className="flex flex-1 flex-col gap-xs text-body text-ink" htmlFor="invite-email">
          Invite reception staff by email
          <input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-input border border-border bg-surface px-md py-sm text-body text-ink"
          />
        </label>
        <button
          type="submit"
          disabled={inviteMutation.isPending}
          className="rounded-button bg-canyon px-lg py-sm font-sans text-body font-bold text-white disabled:opacity-50"
        >
          {inviteMutation.isPending ? 'Sending…' : 'Invite'}
        </button>
      </form>
      {formError ? <p className="mt-sm text-body text-danger">{formError}</p> : null}

      <div className="mt-xl">
        {teamQuery.isLoading ? <PageLoading label="Loading team…" /> : null}
        {teamQuery.isError ? <p className="text-body text-danger">Could not load the team.</p> : null}
        {teamQuery.data ? (
          <table className="w-full text-left text-body">
            <thead>
              <tr className="text-caption uppercase text-ink-3">
                <th className="py-xs">Email</th>
                <th className="py-xs">Role</th>
                <th className="py-xs">Joined</th>
                <th className="py-xs" />
              </tr>
            </thead>
            <tbody>
              {teamQuery.data.members.map((member) => (
                <tr key={member.id} className="border-t border-hair">
                  <td className="py-sm text-ink">{member.email ?? member.userId}</td>
                  <td className="py-sm text-ink-2">{member.role}</td>
                  <td className="py-sm text-ink-2">{new Date(member.createdAt).toLocaleDateString()}</td>
                  <td className="py-sm text-right">
                    {member.role !== 'owner' ? (
                      <button
                        type="button"
                        onClick={() => revokeMutation.mutate(member.id)}
                        disabled={revokeMutation.isPending}
                        className="text-caption text-danger underline disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </section>
  );
}
